import { useEffect, useMemo, useState } from 'react';
import {
  applyExpenseChange,
  exceedsTotalEstimatedCosts,
  ExpensesTotal,
  FundingInputMode,
  ProjectCardFormProps,
  sumFundingBreakdown,
  toExpenseNumber,
  TOTAL_EXPENSES_EXCEEDED_ERROR
} from '../ProjectCardForm.model';
import { Alert, Box, Center, Divider, Flex, Grid, Space } from '@mantine/core';
import { CustomRadioGroup } from '../../CustomInputFields/CustomRadioGroup/CustomRadioGroup';
import classes from '../ProjectCardForm.module.css';
import { CustomText } from '../../CustomText/CustomText';
import { CustomTextInput } from '../../CustomInputFields/CustomTextInput/CustomTextInput';
import { CustomNumberInput } from '../../CustomInputFields/CustomNumberInput/CustomNumberInput';
import { ExpensesType } from '../../../build/generated-sources/openapi';
import { CustomNumberInputWithValue } from '../../CustomInputFields/CustomNumberInput/CustomNumberInputWithValue';
import { NotificationType, showNotification } from '../../../core/notifications/Notifications';
import { Text } from '@mantine/core';
import { CustomTooltip } from '../../CustomTooltip/CustomTooltip';
import { getExpensesYears, getSustainabilityTableYears } from '../../../core/util/Util';

export const FinancialPlanForm = ({
  form,
  combinedExpenses,
  updateCombinedExpenses,
  updateExpensesData,
  disabled
}: ProjectCardFormProps) => {
  const [expensesTotal, setExpensesTotal] = useState<ExpensesTotal>({
    considerableExpensesTotal: 0,
    ineligibleExpensesTotal: 0,
    budgetEntitlementTotal: 0,
    operatingCostsTotal: 0,
    sustainabilityCostsTotal: 0,
    subsidyReturnTotal: 0,
    scheduledDepositsReceivedTotal: 0,
    totalExpenses: 0,
    totalBudgetEntitlement: 0,
    europeanTotal: 0,
    stateBudgetTotal: 0,
    otherForeignSourcesTotal: 0,
    ownResourcesTotal: 0,
    investmentTotal: 0,
    nonInvestmentTotal: 0
  });

  useEffect(() => {
    const expenseTypeTotal = {
      CONSIDERABLE_EXPENSES: 0,
      INELIGIBLE_EXPENSES: 0,
      BUDGET_ENTITLEMENT: 0,
      OPERATING_COSTS: 0,
      SUSTAINABILITY_COSTS: 0,
      SUBSIDY_RETURN: 0,
      SCHEDULED_DEPOSITS_RECEIVED: 0,
      INVESTMENT: 0,
      NON_INVESTMENT: 0
    };

    combinedExpenses.forEach((yearData) => {
      yearData.forEach((expenseData) => {
        const { expenseType, expense } = expenseData;
        if (expenseType) {
          // [DAP-032] toExpenseNumber — bez něj se řetězce z NumberInput zřetězí místo sečtení.
          expenseTypeTotal[expenseType as keyof typeof expenseTypeTotal] +=
            toExpenseNumber(expense);
        }
      });
    });

    const updatedExpensesTotal = {
      considerableExpensesTotal: expenseTypeTotal[ExpensesType.ConsiderableExpenses],
      ineligibleExpensesTotal: expenseTypeTotal[ExpensesType.IneligibleExpenses],
      budgetEntitlementTotal: expenseTypeTotal[ExpensesType.BudgetEntitlement],
      operatingCostsTotal: expenseTypeTotal[ExpensesType.OperatingCosts],
      sustainabilityCostsTotal: expenseTypeTotal[ExpensesType.SustainabilityCosts],
      subsidyReturnTotal: expenseTypeTotal[ExpensesType.SubsidyReturn],
      scheduledDepositsReceivedTotal: expenseTypeTotal[ExpensesType.ScheduledDepositsReceived],
      totalExpenses:
        expenseTypeTotal[ExpensesType.ConsiderableExpenses] +
        expenseTypeTotal[ExpensesType.IneligibleExpenses],
      totalBudgetEntitlement:
        expenseTypeTotal[ExpensesType.IneligibleExpenses] +
        expenseTypeTotal[ExpensesType.BudgetEntitlement],
      europeanTotal:
        ((form.values.european ?? 0) / 100) * expenseTypeTotal[ExpensesType.ConsiderableExpenses] ||
        0,
      stateBudgetTotal:
        ((form.values.stateBudget ?? 0) / 100) *
          expenseTypeTotal[ExpensesType.ConsiderableExpenses] || 0,
      otherForeignSourcesTotal:
        ((form.values.otherForeignSources ?? 0) / 100) *
          expenseTypeTotal[ExpensesType.ConsiderableExpenses] || 0,
      ownResourcesTotal:
        ((form.values.ownResources ?? 0) / 100) *
          expenseTypeTotal[ExpensesType.ConsiderableExpenses] || 0,
      investmentTotal: expenseTypeTotal.INVESTMENT,
      nonInvestmentTotal: expenseTypeTotal.NON_INVESTMENT
    };

    setExpensesTotal(updatedExpensesTotal);
    updateExpensesData(expensesTotal);
  }, [combinedExpenses]);

  // [DAP-032] Mantine NumberInput posílá number | string; applyExpenseChange hodnotu normalizuje.
  const handleFormChange = (value: number | string, year: number, expensesType: string) => {
    const yearData = combinedExpenses.get(year);

    if (!yearData) {
      showNotification(
        'Při načítání finančních údajů došlo k problému',
        'financial-data',
        NotificationType.ERROR
      );
      return;
    }

    // DAP-017: applyExpenseChange položku daného typu aktualizuje, a pokud pro daný rok
    // chybí (rok bez podkladových řádků v DB), vytvoří ji — dřív se hodnota tiše zahodila.
    updateCombinedExpenses(year, applyExpenseChange(yearData, year, value, expensesType));
  };

  const getYearExpense = (yearData: any[], type: string): number =>
    toExpenseNumber(yearData.find((e) => e.expenseType === type)?.expense);

  // [DAP-031] Celkové výdaje nesmí přesáhnout celkové (předpokládané) náklady projektu.
  const totalExpensesExceeded = exceedsTotalEstimatedCosts(
    expensesTotal.totalExpenses,
    form.values.totalEstimatedCosts
  );

  const formatAmount = (value: number): string => `${value.toLocaleString('cs-CZ')} Kč`;

  const implementationYears = useMemo(() => {
    return getExpensesYears(
      form.values.preprojectPreparationStart,
      form.values.implementationStart,
      form.values.implementationEnd
    );
  }, [
    form.values.preprojectPreparationStart,
    form.values.implementationStart,
    form.values.implementationEnd
  ]);

  // [DAP-040] 2. tabulka začíná stejně jako tabulka výdajů (příprava/realizace) a končí ukončením
  // udržitelnosti — příjmy dotací ex-post i zálohy přicházejí i v době realizace.
  const sustainabilityYears = useMemo(() => {
    return (
      getSustainabilityTableYears(
        form.values.preprojectPreparationStart,
        form.values.implementationStart,
        form.values.sustainabilityPeriodStart,
        form.values.sustainabilityPeriodEnd
      ) ?? implementationYears
    );
  }, [
    form.values.preprojectPreparationStart,
    form.values.implementationStart,
    form.values.sustainabilityPeriodStart,
    form.values.sustainabilityPeriodEnd,
    implementationYears
  ]);

  const implementationEntries = useMemo(() => {
    const yearSet = new Set(implementationYears);
    return Array.from(combinedExpenses).filter(([year]) => yearSet.has(year));
  }, [combinedExpenses, implementationYears]);

  const sustainabilityEntries = useMemo(() => {
    const yearSet = new Set(sustainabilityYears);
    return Array.from(combinedExpenses).filter(([year]) => yearSet.has(year));
  }, [combinedExpenses, sustainabilityYears]);

  const implementationExpenseTypes = new Set([
    'CONSIDERABLE_EXPENSES',
    'INELIGIBLE_EXPENSES',
    'INVESTMENT',
    'NON_INVESTMENT',
    'BUDGET_ENTITLEMENT'
  ]);

  const sustainabilityExpenseTypes = new Set([
    'OPERATING_COSTS',
    'SUSTAINABILITY_COSTS',
    'SUBSIDY_RETURN',
    'SCHEDULED_DEPOSITS_RECEIVED'
  ]);

  const hiddenNonZeroImplementationYears = useMemo(() => {
    const visibleYears = new Set(implementationYears);
    const years: number[] = [];
    combinedExpenses.forEach((yearData, year) => {
      if (!visibleYears.has(year)) {
        const hasNonZero = yearData.some(
          (e) => implementationExpenseTypes.has(e.expenseType ?? '') && (e.expense ?? 0) !== 0
        );
        if (hasNonZero) years.push(year);
      }
    });
    return years.sort((a, b) => a - b);
  }, [combinedExpenses, implementationYears]);

  const hiddenNonZeroSustainabilityYears = useMemo(() => {
    const visibleYears = new Set(sustainabilityYears);
    const years: number[] = [];
    combinedExpenses.forEach((yearData, year) => {
      if (!visibleYears.has(year)) {
        const hasNonZero = yearData.some(
          (e) => sustainabilityExpenseTypes.has(e.expenseType ?? '') && (e.expense ?? 0) !== 0
        );
        if (hasNonZero) years.push(year);
      }
    });
    return years.sort((a, b) => a - b);
  }, [combinedExpenses, sustainabilityYears]);

  // DAP-022: režim zadávání rozpadu financování (procenta / pevná částka).
  // Uloženým zdrojem pravdy zůstávají procenta – v režimu NOMINAL se zadaná částka
  // přepočítá na procento (částka / základ * 100) a zapíše do procentního pole formuláře.
  const fundingInputMode = form.values.fundingInputMode ?? FundingInputMode.PERCENT;
  const isNominalMode = fundingInputMode === FundingInputMode.NOMINAL;
  // Základ pro přepočet % <-> částka je totožný se základem pro zobrazení částky.
  const fundingBase = expensesTotal.considerableExpensesTotal;
  // [DAP-022] Oba režimy (procenta i pevná částka) jsou vždy použitelné a přepínatelné, včetně
  // nulového základu — přepínač ani pole se nezamykají a režim se nikam nevynucuje. Zamyká se
  // jen podle aktivního režimu (edituje se buď procento, nebo částka) a podle toho, zda je celý
  // formulář jen pro čtení (disabled):
  //   - v režimu PROCENTA jsou editovatelná procentní pole (částky jen zobrazujeme),
  //   - v režimu PEVNÁ ČÁSTKA jsou editovatelná pole s částkou (procenta se dopočtou).
  // Při nulovém základu zůstává pole pevné částky editovatelné, ale rozpad nuly je nula: zadaná
  // částka se nepromítne (částka/0 nelze převést na procento — ošetřeno v handleNominalAmountChange)
  // a zobrazí se 0; jakmile je základ > 0, částky i procenta se dopočítají normálně.
  const percentDisabled = disabled || isNominalMode;
  const nominalDisabled = disabled;

  // Přepočet zadané částky na procento a zápis do procentního pole formuláře.
  const handleNominalAmountChange = (amount: number, percentFieldName: string) => {
    if (!fundingBase) {
      return;
    }
    const percent = (Number(amount) / fundingBase) * 100;
    form.setFieldValue(percentFieldName, percent);
  };

  // Review-finding-1: zobrazované částky rozpadu financování odvozujeme REAKTIVNĚ z
  // aktuálních procent ve formuláři a základu (uznatelné výdaje celkem), nikoli jen z
  // expensesTotal (ten se přepočítává pouze při změně combinedExpenses). Bez toho by se
  // v režimu NOMINAL zadaná částka po zápisu procenta vizuálně vracela na starou hodnotu.
  //
  // V režimu PERCENT: uživatel edituje procento přímo → částka = procento/100 * základ.
  // V režimu NOMINAL: uživatel edituje částku → uloží se procento = částka/základ*100 →
  //   zobrazená částka = procento/100 * základ = zadaná částka (round-trip je přesný,
  //   protože procento držíme jako plné float bez zaokrouhlení). Zobrazujeme tedy hodnotu
  //   konzistentní s uloženým procentem.
  const fundingAmounts = useMemo(() => {
    const amountFromPercent = (percent: number | undefined) =>
      ((percent ?? 0) / 100) * fundingBase || 0;

    const europeanTotal = amountFromPercent(form.values.european);
    const stateBudgetTotal = amountFromPercent(form.values.stateBudget);
    const otherForeignSourcesTotal = amountFromPercent(form.values.otherForeignSources);
    const ownResourcesTotal = amountFromPercent(form.values.ownResources);
    // [DAP-035] fundingBreakdownTotal: čtyři procentní zdroje + ručně zadané „Vlastní zdroje
    // (nezpůsobilé výdaje)". Ta částka se od DAP-035 zadává v kartě a na procentech nezávisí
    // (dřív se dopočítávala ze součtu neuznatelných výdajů). Paušál (lumpSum) se NEzapočítává.
    const fundingBreakdownTotal = sumFundingBreakdown({
      europeanTotal,
      stateBudgetTotal,
      otherForeignSourcesTotal,
      ownResourcesTotal,
      ownResourcesIneligibleAmount: form.values.ownResourcesIneligibleAmount
    });

    return {
      europeanTotal,
      stateBudgetTotal,
      otherForeignSourcesTotal,
      ownResourcesTotal,
      fundingBreakdownTotal
    };
  }, [
    fundingBase,
    form.values.european,
    form.values.stateBudget,
    form.values.otherForeignSources,
    form.values.ownResources,
    form.values.ownResourcesIneligibleAmount
  ]);

  return (
    <>
      <Box className={classes['inner-box']}>
        <Grid grow>
          <Grid.Col span={12}>
            <Space h={'xl'}></Space>
            {hiddenNonZeroImplementationYears.length > 0 && (
              <Alert color="orange" mb="md">
                Projekt obsahuje nenulové výdaje mimo zobrazovaný rozsah let:{' '}
                <strong>{hiddenNonZeroImplementationYears.join(', ')}</strong>. Tyto výdaje nejsou
                zobrazeny, ale budou zachovány při uložení.
              </Alert>
            )}
            <Grid columns={17}>
              <Grid.Col span={2} />
              <Grid.Col span={3}>
                <Center>
                  <CustomText label="uznatelné výdaje (způsobilé)" />
                </Center>
              </Grid.Col>
              <Grid.Col span={3}>
                <Center>
                  <CustomText label="neuznatelné výdaje (nezpůsobilé)" />
                </Center>
              </Grid.Col>
              <Grid.Col span={3}>
                <Center>
                  <CustomText label="investiční (nárok na rozpočet kraje)" />
                </Center>
              </Grid.Col>
              <Grid.Col span={3}>
                <Center>
                  <CustomText label="neinvestiční (nárok na rozpočet kraje)" />
                </Center>
              </Grid.Col>
              <Grid.Col span={3}>
                <Center>
                  <CustomText label="nárok na rozpočet kraje (celkem)" />
                </Center>
              </Grid.Col>
            </Grid>
            {implementationEntries.map(([year, yearData]) => {
              const considerable = getYearExpense(yearData, 'CONSIDERABLE_EXPENSES');
              const ineligible = getYearExpense(yearData, 'INELIGIBLE_EXPENSES');
              const investment = getYearExpense(yearData, 'INVESTMENT');
              const nonInvestment = getYearExpense(yearData, 'NON_INVESTMENT');
              const budgetEntitlement = getYearExpense(yearData, 'BUDGET_ENTITLEMENT');

              return (
                <div key={year}>
                  <Grid key={year} columns={17}>
                    <Grid.Col span={2}>
                      <CustomText label={year.toString()} mt={15} />
                    </Grid.Col>

                    <Grid.Col span={3}>
                      <CustomNumberInputWithValue
                        disabled={disabled}
                        name={`yearData.${year}.CONSIDERABLE_EXPENSES`}
                        onChange={(val) => handleFormChange(val, year, 'CONSIDERABLE_EXPENSES')}
                        value={considerable}
                      />
                    </Grid.Col>

                    <Grid.Col span={3}>
                      <CustomNumberInputWithValue
                        disabled={disabled}
                        name={`yearData.${year}.INELIGIBLE_EXPENSES`}
                        onChange={(val) => handleFormChange(val, year, 'INELIGIBLE_EXPENSES')}
                        value={ineligible}
                      />
                    </Grid.Col>

                    <Grid.Col span={3} className={classes['expenses-separator']}>
                      <CustomNumberInputWithValue
                        disabled={disabled}
                        name={`yearData.${year}.INVESTMENT`}
                        onChange={(val) => handleFormChange(val, year, 'INVESTMENT')}
                        value={investment}
                      />
                    </Grid.Col>

                    <Grid.Col span={3}>
                      <CustomNumberInputWithValue
                        disabled={disabled}
                        name={`yearData.${year}.NON_INVESTMENT`}
                        onChange={(val) => handleFormChange(val, year, 'NON_INVESTMENT')}
                        value={nonInvestment}
                      />
                    </Grid.Col>

                    <Grid.Col span={3}>
                      <CustomNumberInputWithValue
                        disabled
                        name={`yearData.${year}.BUDGET_ENTITLEMENT`}
                        value={budgetEntitlement}
                      />
                    </Grid.Col>
                  </Grid>
                </div>
              );
            })}

            <Grid columns={17} align="center">
              <Grid.Col span={2}>
                <CustomText label="Celkem" mt={15} />
              </Grid.Col>

              <Grid.Col span={3}>
                <CustomNumberInputWithValue
                  disabled
                  value={expensesTotal.considerableExpensesTotal}
                />
              </Grid.Col>

              <Grid.Col span={3}>
                <CustomNumberInputWithValue
                  disabled
                  value={expensesTotal.ineligibleExpensesTotal}
                />
              </Grid.Col>

              <Grid.Col span={3} className={classes['expenses-separator']}>
                <CustomNumberInputWithValue disabled value={expensesTotal.investmentTotal ?? 0} />
              </Grid.Col>

              <Grid.Col span={3}>
                <CustomNumberInputWithValue
                  disabled
                  value={expensesTotal.nonInvestmentTotal ?? 0}
                />
              </Grid.Col>

              <Grid.Col span={3}>
                <CustomNumberInputWithValue disabled value={expensesTotal.budgetEntitlementTotal} />
              </Grid.Col>
            </Grid>

            <Grid columns={17} align="center">
              <Grid.Col span={5}>
                <CustomText label={'Celkové výdaje :'} mt={15} />
              </Grid.Col>
              <Grid.Col span={3}>
                {/* DAP-031: "Celkové výdaje" = uznatelné celkem + neuznatelné celkem.
                    Dřív se tu zobrazoval totalBudgetEntitlement (neuznatelné + nárok
                    na rozpočet kraje) — to byla chybná veličina. */}
                <CustomNumberInputWithValue disabled value={expensesTotal.totalExpenses} />
              </Grid.Col>
              <Grid.Col span={6}></Grid.Col>
            </Grid>

            {/* [DAP-031] Tvrdé pravidlo — uložení blokuje onFormSubmit v ProjectCard. */}
            {totalExpensesExceeded && (
              <Alert color="red" mt="md">
                {TOTAL_EXPENSES_EXCEEDED_ERROR} (celkové výdaje{' '}
                <strong>{formatAmount(expensesTotal.totalExpenses)}</strong>, celkové
                (předpokládané) náklady{' '}
                <strong>{formatAmount(form.values.totalEstimatedCosts ?? 0)}</strong>). Projekt
                nepůjde uložit, dokud to nespravíte.
              </Alert>
            )}
          </Grid.Col>
        </Grid>
        <Divider my="md" color={'black'} />
        <div className={classes.pageBreak} />
        <Grid grow gutter={40} align={'start'} pb={10}>
          <Grid.Col span={6}>
            <Text size="md" mb={20}>
              Rozpad financování
            </Text>
            <CustomRadioGroup
              label="Režim zadávání"
              disabled={disabled}
              inline
              options={[
                { label: 'Procenta', value: FundingInputMode.PERCENT },
                { label: 'Pevná částka', value: FundingInputMode.NOMINAL }
              ]}
              value={fundingInputMode}
              onChange={(val) => form.setFieldValue('fundingInputMode', val as FundingInputMode)}
            />
            <div className={classes.printHidden}>
              <Grid columns={1}>
                <Grid.Col span={1}>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                    <Text size="sm" fw={500}>
                      Evropská
                    </Text>
                    <CustomTooltip label={'Aktualizace pole proběhne po uložení'} />
                  </div>
                  <Flex gap={8} p={0} style={{ width: '100%' }} align={'end'}>
                    <CustomNumberInput
                      disabled={percentDisabled}
                      rightSection="%"
                      inputProps={form.getInputProps('european')}
                      width={120}
                    />
                    <Box style={{ flex: 1 }}>
                      <CustomNumberInputWithValue
                        disabled={isNominalMode ? nominalDisabled : true}
                        value={fundingAmounts.europeanTotal}
                        onChange={
                          isNominalMode
                            ? (val) => handleNominalAmountChange(Number(val), 'european')
                            : undefined
                        }
                      />
                    </Box>
                  </Flex>
                </Grid.Col>

                <Grid.Col span={1}>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                    <Text size="sm" fw={500}>
                      Státní rozpočet
                    </Text>
                    <CustomTooltip label={'Aktualizace pole proběhne po uložení'} />
                  </div>
                  <Flex gap={8} p={0} style={{ width: '100%' }} align={'end'}>
                    <CustomNumberInput
                      disabled={percentDisabled}
                      rightSection="%"
                      inputProps={form.getInputProps('stateBudget')}
                      width={120}
                    />
                    <Box style={{ flex: 1 }}>
                      <CustomNumberInputWithValue
                        disabled={isNominalMode ? nominalDisabled : true}
                        value={fundingAmounts.stateBudgetTotal}
                        onChange={
                          isNominalMode
                            ? (val) => handleNominalAmountChange(Number(val), 'stateBudget')
                            : undefined
                        }
                      />
                    </Box>
                  </Flex>
                </Grid.Col>

                <Grid.Col span={1}>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                    <Text size="sm" fw={500}>
                      Z toho paušál
                    </Text>
                  </div>
                  <Flex gap={8} p={0} style={{ width: '100%' }} align={'end'}>
                    <CustomNumberInput
                      disabled={disabled}
                      inputProps={form.getInputProps('lumpSum')}
                      width={120}
                    />
                  </Flex>
                </Grid.Col>

                <Grid.Col span={1}>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                    <Text size="sm" fw={500}>
                      Jiné cizí zdroje
                    </Text>
                    <CustomTooltip label={'Aktualizace pole proběhne po uložení'} />
                  </div>
                  <Flex gap={8} p={0} style={{ width: '100%' }} align={'end'}>
                    <CustomNumberInput
                      disabled={percentDisabled}
                      rightSection="%"
                      inputProps={form.getInputProps('otherForeignSources')}
                      width={120}
                    />
                    <Box style={{ flex: 1 }}>
                      <CustomNumberInputWithValue
                        disabled={isNominalMode ? nominalDisabled : true}
                        value={fundingAmounts.otherForeignSourcesTotal}
                        onChange={
                          isNominalMode
                            ? (val) => handleNominalAmountChange(Number(val), 'otherForeignSources')
                            : undefined
                        }
                      />
                    </Box>
                  </Flex>
                </Grid.Col>

                <Grid.Col span={1}>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                    <Text size="sm" fw={500}>
                      Vlastní zdroje (způsobilé výdaje)
                    </Text>
                    <CustomTooltip label={'Aktualizace pole proběhne po uložení'} />
                  </div>
                  <Flex gap={8} p={0} style={{ width: '100%' }} align={'end'}>
                    <CustomNumberInput
                      disabled={percentDisabled}
                      rightSection="%"
                      inputProps={form.getInputProps('ownResources')}
                      width={120}
                    />
                    <Box style={{ flex: 1 }}>
                      <CustomNumberInputWithValue
                        disabled={isNominalMode ? nominalDisabled : true}
                        value={fundingAmounts.ownResourcesTotal}
                        onChange={
                          isNominalMode
                            ? (val) => handleNominalAmountChange(Number(val), 'ownResources')
                            : undefined
                        }
                      />
                    </Box>
                  </Flex>
                </Grid.Col>

                <Grid.Col span={1}>
                  <CustomNumberInput
                    disabled={disabled}
                    label={'Vlastní zdroje (nezpůsobilé výdaje)'}
                    inputProps={form.getInputProps('ownResourcesIneligibleAmount')}
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <CustomNumberInputWithValue
                    disabled
                    label={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        Celkem
                        <CustomTooltip label={'Aktualizace pole proběhne po uložení'} />
                      </div>
                    }
                    value={fundingAmounts.fundingBreakdownTotal}
                  />
                </Grid.Col>
              </Grid>
              <Grid columns={1}></Grid>
            </div>
            <div className={classes.formHidden}>
              <CustomText mt={15} label="Evropská"></CustomText>
              <CustomNumberInput
                disabled={disabled}
                rightSection={'%'}
                width={80}
                inputProps={form.getInputProps('european')}
              />
              <CustomNumberInputWithValue disabled value={fundingAmounts.europeanTotal} />
              <CustomText mt={15} label="Státní rozpočet"></CustomText>
              <CustomNumberInput
                rightSection={'%'}
                width={80}
                disabled={disabled}
                inputProps={form.getInputProps('stateBudget')}
              />
              <CustomNumberInputWithValue disabled value={fundingAmounts.stateBudgetTotal} />
              <CustomText mt={15} label="Z toho paušál"></CustomText>
              <CustomNumberInput
                width={80}
                disabled={disabled}
                inputProps={form.getInputProps('lumpSum')}
              />
              <CustomText mt={15} label="Jiné cizí zdroje"></CustomText>
              <CustomNumberInput
                rightSection={'%'}
                width={80}
                disabled={disabled}
                inputProps={form.getInputProps('otherForeignSources')}
              />
              <CustomNumberInputWithValue
                disabled
                value={fundingAmounts.otherForeignSourcesTotal}
              />
              <CustomText mt={15} label="Vlastní zdroje (způsobilé výdaje)"></CustomText>
              <CustomNumberInput
                rightSection={'%'}
                width={80}
                disabled={disabled}
                inputProps={form.getInputProps('ownResources')}
              />
              <CustomNumberInputWithValue disabled value={fundingAmounts.ownResourcesTotal} />
              <CustomText mt={15} label="Vlastní zdroje (nezpůsobilé výdaje)"></CustomText>
              <CustomNumberInputWithValue
                disabled
                value={toExpenseNumber(form.values.ownResourcesIneligibleAmount)}
              />
              <CustomText mt={15} label="Celkem"></CustomText>
              <CustomNumberInputWithValue disabled value={fundingAmounts.fundingBreakdownTotal} />
            </div>
          </Grid.Col>

          <Grid.Col mt={45} span={6}>
            <Grid columns={1}>
              <Grid.Col span={1}>
                <CustomTextInput
                  label="Operační program"
                  disabled={disabled}
                  inputProps={form.getInputProps('operationalProgram')}
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <CustomTextInput
                  label="Prioritní osa"
                  disabled={disabled}
                  inputProps={form.getInputProps('priorityAxis')}
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <CustomTextInput
                  label="Investiční priorita"
                  disabled={disabled}
                  inputProps={form.getInputProps('investmentPriority')}
                />
              </Grid.Col>
              <Grid.Col span={1}>
                <CustomTextInput
                  label="Územní dimenze"
                  disabled={disabled}
                  inputProps={form.getInputProps('territorialDimension')}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
        <Divider my="md" color={'black'} />
        <div className={classes.pageBreak} />
        {hiddenNonZeroSustainabilityYears.length > 0 && (
          <Alert color="orange" mb="md">
            Projekt obsahuje nenulové provozní výdaje mimo zobrazovaný rozsah let:{' '}
            <strong>{hiddenNonZeroSustainabilityYears.join(', ')}</strong>. Tyto výdaje nejsou
            zobrazeny, ale budou zachovány při uložení.
          </Alert>
        )}
        <Grid columns={13}>
          <Grid.Col span={3} offset={1}>
            <Center>
              <CustomText label={'Provozní náklady'} mt={15} />
            </Center>
          </Grid.Col>
          <Grid.Col span={3}>
            <Center>
              <CustomText label={'Náklady na udržitelnost'} mt={15} />
            </Center>
          </Grid.Col>
          <Grid.Col span={3}>
            <Center>
              <CustomText label={'Plánované příjmy dotací (ex-post)'} mt={15} />
            </Center>
          </Grid.Col>
          <Grid.Col span={3}>
            <Center>
              <CustomText label={'Plánované přijaté zálohy'} mt={15} />
            </Center>
          </Grid.Col>
        </Grid>
        {sustainabilityEntries.map(([year, yearData]) => (
          <div key={year}>
            <Grid key={year} columns={13}>
              <Grid.Col span={1}>
                <CustomText label={year.toString()} mt={15} />
              </Grid.Col>
              <Grid.Col span={3}>
                <CustomNumberInputWithValue
                  disabled={disabled}
                  name={`yearData.${year}.OPERATING_COSTS`}
                  onChange={(event) => handleFormChange(event, year, 'OPERATING_COSTS')}
                  value={
                    yearData.find((expense) => expense.expenseType === 'OPERATING_COSTS')?.expense
                  }
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <CustomNumberInputWithValue
                  disabled={disabled}
                  name={`yearData.${year}.SUSTAINABILITY_COSTS`}
                  onChange={(event) => handleFormChange(event, year, 'SUSTAINABILITY_COSTS')}
                  value={
                    yearData.find((expense) => expense.expenseType === 'SUSTAINABILITY_COSTS')
                      ?.expense
                  }
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <CustomNumberInputWithValue
                  disabled={disabled}
                  name={`yearData.${year}.SUBSIDY_RETURN`}
                  onChange={(event) => handleFormChange(event, year, 'SUBSIDY_RETURN')}
                  value={
                    yearData.find((expense) => expense.expenseType === 'SUBSIDY_RETURN')?.expense
                  }
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <CustomNumberInputWithValue
                  disabled={disabled}
                  name={`yearData.${year}.SCHEDULED_DEPOSITS_RECEIVED`}
                  onChange={(event) => handleFormChange(event, year, 'SCHEDULED_DEPOSITS_RECEIVED')}
                  value={
                    yearData.find(
                      (expense) => expense.expenseType === 'SCHEDULED_DEPOSITS_RECEIVED'
                    )?.expense
                  }
                />
              </Grid.Col>
            </Grid>
          </div>
        ))}
        <Grid columns={13}>
          <Grid.Col span={3} offset={1}>
            <CustomNumberInputWithValue
              label={'celkem'}
              disabled
              value={expensesTotal.operatingCostsTotal}
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <CustomNumberInputWithValue
              label={'celkem'}
              disabled
              value={expensesTotal.sustainabilityCostsTotal}
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <CustomNumberInputWithValue
              label={'celkem'}
              disabled
              value={expensesTotal.subsidyReturnTotal}
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <CustomNumberInputWithValue
              label={'celkem'}
              disabled
              value={expensesTotal.scheduledDepositsReceivedTotal}
            />
          </Grid.Col>
        </Grid>
        <Divider my="md" color={'black'} />
      </Box>
      <div className={classes.pageBreak} />
    </>
  );
};
